/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import Stack from "../components/ui/Stack";
import Icon from "../components/ui/Icon";
import Title from "../components/ui/Title";
import Text from "../components/ui/Text";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import { ROUTES, BOOKING_STATUS as BOOKING_STATUS_VALUES } from "../constants/constants";
import { useStepManager } from "../hooks/useStepManager";
import AddToCalendarModal from "../components/booking/AddToCalendarModal";
import Button from "../components/ui/Button";
import BookingSuccessRecurringSessions from "../components/booking/BookingSuccessRecurringSessions";
import OrderDetails from "../components/booking/OrderDetails";
import InvoicePackageInstances from "../components/booking/checkout/InvoicePackageInstances";
import ButtonGroup from "../components/ui/ButtonGroup";
import CancelationModal from "../components/booking/CancelationModal";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import useWidgetCallbackParams from "../hooks/useWidgetCallbackParams";
import LoadingIndicator from "../components/LoadingIndicator";
import { useToast } from "../components/ui/Toast";
import { useBookingStatusPage } from "../hooks/useBookingStatusPage";
import FormatDateTime from "../components/formatters/FormatDateTime";
import FormatDatePeriod from "../components/formatters/FormatDatePeriod";
import { useBooking } from "../hooks/useBooking";
import { useInvoiceCalculations } from "../hooks/checkout/useInvoiceCalculations";
import getStatusConfig from "../utils/getStatusConfig";
import { extractErrorMessage } from "../utils/api/apiErrors";
import { canRetryPayment } from "../utils/helpers/invoice";

const StyledBookingStatus = styled(Stack)`
  margin-top: 48px;
`;

const BookingStatusInfo = styled(Stack)`
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  text-align: center;
`;

const BookingDateLabel = styled.div`
  width: 100%;
  padding: 8px;
  border-radius: 8px;
  text-align: center;
  background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.1);
`;

const BookingStatus = ({ status: initialStatus, source }) => {
  const { navigateToStep } = useStepManager();
  const navigate = useNavigate();
  const { bookingResponse, resetBookingRequest } = useBooking();
  const {
    invoiceId: callbackInvoiceId,
    serviceIds,
    location: preselectLocation,
    provider: preselectProvider,
    promotionCode: preselectCoupon,
    clearParams,
  } = useWidgetCallbackParams();
  const routerState = useLocation().state;
  const invoiceId = callbackInvoiceId || routerState?.invoiceId || null;
  const hadPreselects =
    serviceIds.length > 0 || !!preselectLocation || !!preselectProvider || !!preselectCoupon;
  const { toast } = useToast();

  const {
    isLoading,
    isError,
    error,
    invoice,
    bookings,
    firstBooking,
    lastBooking,
    bookingEntities,
    bookingStatus,
    isBookingDelay,
    canBeCancelled,
    canBeRescheduled,
    shouldShowBookingDetails,
    shouldShowAddToCalendar,
    shouldShowDateLabels,
    shouldShowOnlineMeetingLink,
  } = useBookingStatusPage({
    invoiceId,
    bookingResponse,
    initialStatus,
    source,
  });

  const { hasRestAmountWithDeposit, price, deposit, discount, tip, taxes, hasTaxes } = useInvoiceCalculations(invoice);
  const isRescheduleSuccess = initialStatus === BOOKING_STATUS_VALUES.RESCHEDULE_SUCCESS;
  const statusConfig = useMemo(
    () => getStatusConfig(isRescheduleSuccess ? BOOKING_STATUS_VALUES.RESCHEDULE_SUCCESS : bookingStatus, !!invoice),
    [bookingStatus, invoice, isRescheduleSuccess],
  );

  // Pay later still owes the payment; a saved-method charge awaiting the processor does not.
  const showPayNow = isBookingDelay || canRetryPayment(invoice);

  // On CONFIRMED with preselects we go back to Home. Keep Redux intact so
  // the booking flow opens with the same service/provider preselected.
  const willBounceToHome = hadPreselects && bookingStatus === BOOKING_STATUS_VALUES.CONFIRMED;

  useEffect(() => {
    if (!isLoading) {
      clearParams();
      if (!willBounceToHome) {
        resetBookingRequest();
        sessionStorage.removeItem("sb_active_flow");
      }
    }
  }, [clearParams, isLoading, resetBookingRequest, willBounceToHome]);

  useEffect(() => {
    if (isError && error?.response?.data?.message) {
      toast({
        title: "Error",
        description: `There was an error loading your invoice. Reason: ${extractErrorMessage(error)}`,
        variant: "error",
      });
    }
  }, [isError, error, toast]);

  const handleMakeAnotherBooking = () => {
    navigateToStep(ROUTES.HOME);
  };

  const handlePayNow = () => {
    navigate(ROUTES.INVOICE_PAY.replace(":id", invoice.id), {
      replace: true,
      state: { from: ROUTES.HOME },
    });
  };

  const handleReschedule = () => {
    if (!firstBooking) return;
    navigate(ROUTES.RESCHEDULE_BOOKING.replace(":id", firstBooking.id));
  };

  if (isError) {
    return <Navigate replace to={ROUTES.HOME} />;
  }

  if (isLoading || !bookingStatus) {
    return (
      <Container>
        <NavigationHeader />
        <LoadingIndicator page />
      </Container>
    );
  }

  // Payment OK + preselects present: go to Home, the booking flow picks up
  // the preselected service/location from Redux (set by
  // useSchedulerInitialization). On failure stay here.
  if (willBounceToHome) {
    return <Navigate replace to={ROUTES.HOME} />;
  }

  return (
    <Container>
      <NavigationHeader navigation cart clientLogin />
      <StyledBookingStatus $gap="1.5rem" $align="center">
        <BookingStatusInfo $align="center">
          {statusConfig?.icon}
          {statusConfig?.title && <Title size="h3">{statusConfig.title}</Title>}
          {statusConfig?.message && <Text color="dark">{statusConfig.message}</Text>}

          {shouldShowDateLabels && firstBooking && (
            <Stack $gap="0.25rem" $align="center" style={{ width: "100%" }}>
              <BookingDateLabel>
                <Text color="dark">
                  <FormatDateTime value={firstBooking.start_datetime} format={FormatDateTime.FORMAT.DATE} />,{" "}
                  {firstBooking.duration && (
                    <FormatDatePeriod
                      from={firstBooking.start_datetime}
                      to={lastBooking.end_datetime}
                      format={FormatDatePeriod.FORMAT.TIME}
                    />
                  )}
                  {!firstBooking.duration && (
                    <FormatDateTime value={firstBooking.start_datetime} format={FormatDatePeriod.FORMAT.TIME} />
                  )}
                </Text>
              </BookingDateLabel>
            </Stack>
          )}
        </BookingStatusInfo>

        {shouldShowAddToCalendar && firstBooking && (
          <AddToCalendarModal
            bookings={bookings}
            bookingEntities={bookingEntities}
            onlineMeetingLink={firstBooking.online_meeting_link}
            trigger={
              <Button variant="secondary" title="add_to_calendar" icon={<Icon.CalendarPlus size={Icon.SIZE.MD} />} />
            }
          />
        )}

        {shouldShowBookingDetails && firstBooking && (
          <>
            {firstBooking?.recurring_sessions?.length > 0 && (
              <BookingSuccessRecurringSessions
                sessions={firstBooking.recurring_sessions}
                bookingEntities={bookingEntities}
              />
            )}
            <OrderDetails
              services={bookingEntities.services}
              provider={bookingEntities.provider}
              products={bookingEntities.products}
              recurring={firstBooking.recurring_sessions}
              price={price}
              tip={tip}
              deposit={hasRestAmountWithDeposit ? deposit : null}
              discount={discount}
              taxes={hasTaxes ? taxes : null}
              bookings={shouldShowOnlineMeetingLink ? bookings : undefined}
            />
            <InvoicePackageInstances invoice={invoice} />
          </>
        )}

        <ButtonGroup displayAs="column" itemsWidth="full">
          {showPayNow && (
            <Button variant="primary" title="Pay now" onClick={handlePayNow} />
          )}
          <Button
            variant={showPayNow ? "secondary" : "primary"}
            title="Make another booking"
            onClick={handleMakeAnotherBooking}
          />
          {canBeRescheduled && firstBooking && (
            <Button
              variant="transparent"
              title="Reschedule booking"
              icon={<Icon.CalendarEmpty />}
              onClick={handleReschedule}
            />
          )}
          {canBeCancelled && firstBooking && <CancelationModal trigger={<Button variant="danger" title="cancel booking" />} booking={firstBooking} />}
        </ButtonGroup>
      </StyledBookingStatus>
    </Container>
  );
};

export default BookingStatus;
